import React, { useRef, useState } from 'react';
import { UploadCloud, File as FileIcon, X } from 'lucide-react';
import { formatBytes } from '../api';

const MAX_BYTES = 250 * 1024 * 1024; // mirrors the multer limit on sopRoutes

/**
 * Drop target for the SOP uploader.
 *
 * Intentionally accepts every file type — normalizing whatever lands here is
 * the tile's whole premise, and anything the converter can't render is still
 * stored and offered as a download. The only rejection is the size ceiling,
 * checked here so the user finds out before a 250MB upload round-trips.
 */
const FileDropzone = ({ file, onFile, disabled = false }) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const accept = (candidate) => {
    if (!candidate) return;
    if (candidate.size > MAX_BYTES) {
      setError(`"${candidate.name}" is ${formatBytes(candidate.size)} — the limit is 250 MB.`);
      return;
    }
    setError('');
    onFile(candidate);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    accept(e.dataTransfer.files?.[0]);
  };

  if (file) {
    return (
      <div className="flex items-center gap-3 p-4 bg-slate-900 border border-slate-700 rounded-lg">
        <FileIcon className="w-8 h-8 text-indigo-400 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-100 truncate">{file.name}</p>
          <p className="text-xs text-slate-400">{formatBytes(file.size)}</p>
        </div>
        <button
          type="button"
          onClick={() => { onFile(null); setError(''); }}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"
          aria-label="Remove file"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`w-full flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-lg
          border-2 border-dashed transition text-center
          ${dragging
            ? 'border-indigo-400 bg-indigo-500/10'
            : 'border-slate-700 bg-slate-900 hover:border-slate-600'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <UploadCloud className={`w-8 h-8 ${dragging ? 'text-indigo-300' : 'text-slate-500'}`} />
        <span className="text-sm text-slate-300">
          Drop a file here, or <span className="text-indigo-400">browse</span>
        </span>
        <span className="text-xs text-slate-500">
          Word, PowerPoint, Excel, PDF, images, video — any format, up to 250 MB
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ''; }}
      />

      {error && <p className="mt-2 text-xs text-rose-400">{error}</p>}
    </div>
  );
};

export default FileDropzone;
